import { LinearGradient } from 'expo-linear-gradient'
import { Redirect, useRouter } from 'expo-router'
import { useEffect, useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { ScrollView, View } from 'react-native'

import { Button, type TDefaultButtonProps } from '@/src/elements/button/default'
import { Card, CardBody } from '@/src/elements/card'
import { Image } from '@/src/elements/image'
import { Icon } from '@/src/elements/media/Icon'
import { GradientTypography } from '@/src/elements/text/gradient'
import { Typography } from '@/src/elements/text/Typography'
import { getIsLoggedIn } from '@/src/features/auth/utils/getIsLoggedIn'
import { routeToPath } from '@/src/general/constants/routes'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { ErrorScreen } from '@/src/general/ui/screens/ErrorScreen'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS } from '@/src/general/utils/gtm/constants/Events.enum'
import type { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants/TrackingSource.enum'
import { useRoutes } from '@/src/general/utils/routes'
import { htmlTagsMap, useTranslate } from '@/src/lang/useTranslate'
import { colors } from '@/src/styles/colors'

import { useBecomeWatchman } from '../../services/useBecomeWatchman'
import { useWatchman } from '../../services/useWatchman'
import { WatchmanEmailInput } from '../components/WatchmanEmailInput'

export type TWatchmanSearchParams = {
  redirect: string | undefined
  source: TRACKING_SOURCE
  theme: string | undefined
}

export const WatchmanScreen = ({ redirect, source: trackingSource, theme }: TWatchmanSearchParams): ReactNode => {
  const [email, setEmail] = useState('')

  const { routes } = useRoutes()
  const router = useRouter()
  const { t } = useTranslate()
  const isLoggedIn = getIsLoggedIn()

  const watchmanService = useWatchman()
  const { mutateAsync, error, isPending } = useBecomeWatchman()

  const handleBecomeWatchman = async () => {
    GTM.sendEvent({ event: EVENTS.WATCHMAN_CTA_CLICKED }, { trackingSource })
    try {
      await mutateAsync()
      router.replace(routes.askWatchmanReward({ theme }))
    } catch (_) {}
  }

  const handleNotNow = () => {
    GTM.sendEvent({ event: EVENTS.WATCHMAN_CTA_REFUSED }, { trackingSource })
    watchmanService.executeBecomeLater()
    const route = redirect || routes.intentions(theme)
    router.navigate(route)
  }

  const handleCreateAccountAsWatchman = () => {
    GTM.sendEvent({ event: EVENTS.WATCHMAN_CTA_CLICKED }, { trackingSource })
    const redirectTo = routeToPath(routes.askWatchmanReward({ action: 'subscribe-watchman' }))
    router.navigate(routes.connectWithEmail({ email, redirectTo }))
  }

  useEffect(() => GTM.sendEvent({ event: EVENTS.WATCHMAN_CTA_DISPLAYED }, { trackingSource }), [trackingSource])

  if (watchmanService.isWatchman) {
    return <Redirect href={routes.watchmanIntentions()} />
  }

  if (error) {
    return <ErrorScreen error={error} defaultErrorKey="common.error.unknown" />
  }

  const becomeButtonCommonProps: TDefaultButtonProps = {
    testID: TEST_ID.WATCHMAN.SCREEN.BECOME_WATCHMAN,
    variant: 'solid',
    colors: 'primary',
    fullWidth: true,
  }

  return (
    <View testID={TEST_ID.WATCHMAN.SCREEN.NAME} className="h-full w-full">
      <ScrollView className="w-full h-full">
        <View className="-my-6 h-60 w-full">
          <Image source={require('@/img/watchman/watchman-candles.webp')} radius="none" contentFit="cover" />
          <LinearGradient
            colors={['rgba(255, 250, 238, 0.00) 0%', colors.primary['bg-light'].DEFAULT]}
            className="absolute bottom-0 h-60 w-full"
          />
        </View>
        <View className="web:mx-auto max-w-md web:items-center gap-8 px-4 pb-4">
          <Icon
            name={ICON.LANTERN}
            className="web:h-16 web:w-16 native:min-h-16 native:max-h-16 translate-y-1 text-primary-icon-contrast-lower native:self-center"
          />
          <GradientTypography className="text-center text-xl font-black md:text-center md:text-2xl" colors="primary">
            {t('intention.prayer.watchman.pray-support')}
          </GradientTypography>
          <View>
            <Typography center>
              <FormattedMessage id="intention.prayer.watchman.engagement.1" values={htmlTagsMap} />
            </Typography>
            <Typography black center size="xl">
              <FormattedMessage id="intention.prayer.watchman.engagement.2" />
            </Typography>
            <Typography center>
              <FormattedMessage id="intention.prayer.watchman.engagement.3" />
            </Typography>
          </View>

          <Card fullWidth shadow="sm">
            <CardBody className="gap-6 p-6 pb-4">
              <Typography colors="primary" contrast="high" className="text-center">
                {t('intention.prayer.watchman.many-watchmen')}
              </Typography>
              <View className="w-full gap-2">
                {isLoggedIn ? (
                  <Button {...becomeButtonCommonProps} isLoading={isPending} onPress={handleBecomeWatchman}>
                    <FormattedMessage id="intention.prayer.watchman.join" />
                  </Button>
                ) : (
                  <WatchmanEmailInput onEmailChange={setEmail} onSubmitEditing={handleCreateAccountAsWatchman}>
                    {({ errorMessage }) => (
                      <Button
                        {...becomeButtonCommonProps}
                        isDisabled={!!errorMessage}
                        onPress={handleCreateAccountAsWatchman}
                      >
                        <FormattedMessage id="intention.prayer.watchman.join" />
                      </Button>
                    )}
                  </WatchmanEmailInput>
                )}
                <Button
                  variant="light"
                  colors="primary"
                  fullWidth
                  onPress={handleNotNow}
                  testID={TEST_ID.WATCHMAN.SCREEN.ASK_LATER}
                >
                  <FormattedMessage id="words.not-now" />
                </Button>
              </View>
            </CardBody>
          </Card>
        </View>
      </ScrollView>
    </View>
  )
}
